import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  fetchNotifications,
  fetchNotificationSettings,
  fetchUnreadCount,
  markNotificationsRead,
  updateNotificationSetting,
} from './api';
import {
  categoryToggleToast,
  NOTIFICATION_CATEGORY_COPY,
  NOTIFICATION_COPY,
  NOTIFICATION_POLICY,
  type NotificationCategory,
} from './constants';
import { groupNotificationsByDate, type NotificationDateGroup } from './grouping';
import type { Notification, NotificationPage, NotificationSettings } from './schemas';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
  /** The bell's count. Its own key, never the feed's — see {@link useUnreadCount}. */
  unread: () => [...notificationKeys.all, 'unread'] as const,
  settings: () => [...notificationKeys.all, 'settings'] as const,
};

/**
 * The notification feed (US-101849 § A, § REFRESH / LOAD / SCROLL).
 *
 * Cursor-paginated and loaded by inline scrolling — the story rules out pagination controls, and
 * `fetchNextPage` is driven from the panel's scroll handler.
 *
 * Unlike the devices listing (which pins `staleTime: Infinity` because its story forbids
 * self-updating), notifications *should* refresh: the red dot has to be able to appear without a
 * manual reload. So the app-wide defaults stand.
 */
export function useNotifications(options: { enabled?: boolean } = {}) {
  const query = useInfiniteQuery({
    queryKey: notificationKeys.list(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchNotifications({ limit: NOTIFICATION_POLICY.PAGE_SIZE, cursor: pageParam }),
    getNextPageParam: (lastPage: NotificationPage) =>
      lastPage.hasNextPage && lastPage.nextCursor ? lastPage.nextCursor : null,
    enabled: options.enabled ?? true,
  });

  const items = useMemo<Notification[]>(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  const groups = useMemo<NotificationDateGroup[]>(() => groupNotificationsByDate(items), [items]);

  // Spreading the query object here would subscribe the caller to every field it carries
  // (`@tanstack/query/no-rest-destructuring`), so the five the panel actually reads are named.
  return {
    items,
    groups,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

/**
 * The number on the bell's badge, and whether there is anything to show at all.
 *
 * Its own request rather than a field on {@link useNotifications}: opening the panel marks
 * everything read, so deriving the badge from the feed would zero it merely by rendering the
 * surface that displays it. `fetchUnreadCount` asks the list for a single row and reads
 * `meta.unreadCount`, which is the whole account's count — not just the rows in hand.
 *
 * A failure resolves to no badge rather than an error: a bell is ambient decoration and must
 * never be the thing that surfaces a transport problem.
 */
export function useUnreadCount() {
  const query = useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: fetchUnreadCount,
  });
  const count = query.data ?? 0;
  return { count, hasUnread: count > 0, isLoading: query.isLoading };
}

/**
 * Back-compat alias for the boolean half of {@link useUnreadCount}.
 *
 * US-101849 § ENTRY POINT specifies a red dot and no numeric count; the badge now shows one
 * anyway, on explicit request, and Tracking Mobile has shown a number since its own bell was
 * rebuilt. Kept so a caller that only needs "is anything unread" does not pull in the number.
 */
export function useHasUnreadNotifications() {
  const { hasUnread, isLoading } = useUnreadCount();
  return { hasUnread, isLoading };
}

/**
 * Mark the account's notifications read, fired when the panel opens.
 *
 * The endpoint takes no ids and marks **every** unread row (see `api.ts`), so the optimistic
 * update zeroes the count rather than decrementing it — there is nothing left unread to keep the
 * badge lit.
 *
 * Crucially this still does **not** restyle the rows the grower is currently looking at. The
 * design (Figma 621:549480) shows the panel with unread rows still tinted, which is only possible
 * if opening it does not immediately repaint them. So the badge clears at once while the fetched
 * rows keep the state they arrived in; the next open returns them read. That is also why only the
 * count query is invalidated on settle — invalidating the feed would refetch it under the open
 * panel and repaint every row.
 */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => markNotificationsRead(),
    // The one write the grower never asks for — it fires because the panel opened, not because
    // anything was clicked. Announcing it would be noise, not feedback, so it stays silent on
    // both halves; the cleared badge below is the only visible effect either way.
    meta: { suppressToast: true },
    onMutate: () => {
      queryClient.setQueryData<number>(notificationKeys.unread(), 0);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unread() });
    },
  });
}

/** The four preference toggles (US-113356). All default on when the server sends nothing. */
export function useNotificationSettings(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: notificationKeys.settings(),
    queryFn: fetchNotificationSettings,
    enabled: options.enabled ?? true,
  });
}

/** The drawer's own title for a category — the word the auto-save toast has to say. */
function categoryTitle(category: NotificationCategory): string {
  return NOTIFICATION_CATEGORY_COPY.find((entry) => entry.category === category)?.title ?? '';
}

/**
 * Auto-save one toggle (US-113356 § AUTO-SAVE & TOAST).
 *
 * Optimistic, with an explicit revert: "Save failure reverts the toggle". The snapshot is
 * captured in `onMutate` and restored in `onError`, so the switch cannot be left showing a state
 * the server rejected. Both toasts are `<MutationToaster>`'s: the success copy names the category,
 * so it is resolved from the mutation's own variables against the drawer's own title list.
 */
export function useUpdateNotificationSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotificationSetting,
    meta: {
      successMessage: ({
        category,
        enabled,
      }: {
        category: NotificationCategory;
        enabled: boolean;
      }) => categoryToggleToast(categoryTitle(category), enabled),
      errorMessage: NOTIFICATION_COPY.SETTINGS_SAVE_FAILED,
    },
    onMutate: async ({ category, enabled }) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.settings() });
      const previous = queryClient.getQueryData<NotificationSettings>(notificationKeys.settings());
      if (previous) {
        queryClient.setQueryData<NotificationSettings>(notificationKeys.settings(), {
          ...previous,
          [category]: enabled,
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationKeys.settings(), context.previous);
      }
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(notificationKeys.settings(), settings);
    },
  });
}

export type { NotificationCategory };
